import { useState } from "react";
import { useGarantias, GarantiaFormData, Garantia } from "@/features/produtos/hooks/useGarantias";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { usePaginacaoLocal } from "@/hooks/usePaginacaoLocal";
import { PaginacaoRodape } from "@/components/PaginacaoRodape";

const EMPTY: GarantiaFormData = { descricao: "", diasGarantia: 0, textoImpressao: "", isInativo: false };

export default function Garantias() {
  const { garantias, isLoading, createGarantia, updateGarantia, deleteGarantia } = useGarantias();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Garantia | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [formData, setFormData] = useState<GarantiaFormData>(EMPTY);
  const [busca, setBusca] = useState("");

  const filtradas = busca
    ? garantias.filter(g => g.descricao?.toLowerCase().includes(busca.toLowerCase()))
    : garantias;

  const paginacao = usePaginacaoLocal(filtradas);

  const handleOpenDialog = (g?: Garantia) => {
    if (g) {
      setEditing(g);
      setFormData({ descricao: g.descricao || "", diasGarantia: g.diasGarantia, textoImpressao: g.textoImpressao || "", isInativo: g.isInativo });
    } else {
      setEditing(null);
      setFormData(EMPTY);
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editing) {
      await updateGarantia.mutateAsync({ id: editing.id, ...formData });
    } else {
      await createGarantia.mutateAsync(formData);
    }
    setDialogOpen(false);
  };

  if (isLoading) return <div className="p-6">Carregando...</div>;

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Garantias</h1>
          <p className="text-muted-foreground">Cadastro de garantias dos produtos</p>
        </div>
        <Button onClick={() => handleOpenDialog()}>
          <Plus className="h-4 w-4 mr-2" />
          Nova Garantia
        </Button>
      </div>

      <div className="max-w-sm">
        <Input placeholder="Buscar por descrição..." value={busca} onChange={e => setBusca(e.target.value)} />
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Descrição</TableHead>
              <TableHead className="w-[140px] text-right">Dias de garantia</TableHead>
              <TableHead className="w-[100px]">Situação</TableHead>
              <TableHead className="w-[100px]">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginacao.itensPaginados.map((g) => (
              <TableRow key={g.id}>
                <TableCell className="font-medium">{g.descricao || "-"}</TableCell>
                <TableCell className="text-right">{g.diasGarantia}</TableCell>
                <TableCell>{g.isInativo ? <span className="text-muted-foreground">Inativa</span> : "Ativa"}</TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => handleOpenDialog(g)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => setDeleteId(g.id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {paginacao.itensPaginados.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                  {busca ? "Nenhuma garantia encontrada para esta busca." : "Nenhuma garantia cadastrada"}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <PaginacaoRodape
        pagina={paginacao.pagina}
        totalPaginas={paginacao.totalPaginas}
        totalItens={paginacao.totalItens}
        primeiroItem={paginacao.primeiroItem}
        ultimoItem={paginacao.ultimoItem}
        tamanhoPagina={paginacao.tamanhoPagina}
        onPaginaChange={paginacao.setPagina}
        onTamanhoPaginaChange={paginacao.setTamanhoPagina}
      />

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? "Editar Garantia" : "Nova Garantia"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="descricao">Descrição *</Label>
              <Input id="descricao" value={formData.descricao} onChange={(e) => setFormData(p => ({ ...p, descricao: e.target.value }))} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="dias">Dias de garantia</Label>
              <Input id="dias" type="number" min="0" value={formData.diasGarantia} onChange={(e) => setFormData(p => ({ ...p, diasGarantia: Number(e.target.value) }))} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="texto">Texto da impressão</Label>
              <Textarea id="texto" rows={3} value={formData.textoImpressao ?? ""} onChange={(e) => setFormData(p => ({ ...p, textoImpressao: e.target.value }))} />
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={!!formData.isInativo} onCheckedChange={(v) => setFormData(p => ({ ...p, isInativo: v }))} />
              <Label>Inativa</Label>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button type="submit">Salvar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir garantia</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir esta garantia? A operação não poderá ser desfeita.
              Se a garantia estiver vinculada a produtos, a exclusão será recusada.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => { if (deleteId) { deleteGarantia.mutate(deleteId); setDeleteId(null); } }}>
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
